import { clipsEqual } from '~/features/clips/equality';
import type { BgColor, Clip, ClipId, KeyedRegion, LayoutMode, Rect } from '~/types/pipeline';
import type { RootState } from './slices';

export type Settings = {
  background: {
    enabled: boolean;
    tolerance: number;
    wandTolerance: number;
    brushRadius: number;
    brushSoftness: number;
    hardEdge: boolean;
    erode: number;
    feather: number;
    despillStrength: number;
    despillRadius: number;
    floodFill: boolean;
    maxEnclosedIsland: number;
    bgColors: BgColor[];
  };
  grid: {
    cropRect: RootState['grid']['cropRect'];
    rowDividers: number[] | null;
    colDividers: number[] | null;
    individualRows: boolean;
    individualCols: boolean;
  };
  editor: {
    showRegions: boolean;
    guidelines: RootState['editor']['guidelines'];
    snapToGuidelines: boolean;
  };
  watermark: { region: RootState['watermark']['region']; enabled: boolean; useOpencv: boolean };
  clips: {
    list: Clip[];
    activeId: ClipId | null;
  };
  frames: {
    pivot: RootState['frames']['pivot'];
  };
  animation: {
    onionStack: boolean;
  };
  source: {
    hash: string | null;
  };
  workflow: {
    step: RootState['workflow']['step'];
    committedKey: string | null;
  };
  // `blobs` is intentionally not persisted — recomputable from cleaned + tolerance + minArea.
  pin: {
    framingMode: RootState['pin']['framingMode'];
    overrides: RootState['pin']['overrides'];
    order: RootState['pin']['order'];
    detectTolerance: number;
    detectMinArea: number;
  };
  atlas: {
    layoutMode: LayoutMode;
  };
};

export function pickSettings(state: RootState): Settings {
  return {
    background: {
      enabled: state.background.enabled,
      tolerance: state.background.tolerance,
      wandTolerance: state.background.wandTolerance,
      brushRadius: state.background.brushRadius,
      brushSoftness: state.background.brushSoftness,
      hardEdge: state.background.hardEdge,
      erode: state.background.erode,
      feather: state.background.feather,
      despillStrength: state.background.despillStrength,
      despillRadius: state.background.despillRadius,
      floodFill: state.background.floodFill,
      maxEnclosedIsland: state.background.maxEnclosedIsland,
      bgColors: state.background.bgColors,
    },
    grid: {
      cropRect: state.grid.cropRect,
      rowDividers: state.grid.rowDividers,
      colDividers: state.grid.colDividers,
      individualRows: state.grid.individualRows,
      individualCols: state.grid.individualCols,
    },
    editor: {
      showRegions: state.editor.showRegions,
      guidelines: state.editor.guidelines,
      snapToGuidelines: state.editor.snapToGuidelines,
    },
    watermark: {
      region: state.watermark.region,
      enabled: state.watermark.enabled,
      useOpencv: state.watermark.useOpencv,
    },
    clips: {
      list: state.clips.list,
      activeId: state.clips.activeId,
    },
    frames: { pivot: state.frames.pivot },
    animation: {
      onionStack: state.animation.onionStack,
    },
    source: {
      hash: state.source.hash,
    },
    workflow: {
      step: state.workflow.step,
      committedKey: state.workflow.committedKey,
    },
    pin: {
      framingMode: state.pin.framingMode,
      overrides: state.pin.overrides,
      order: state.pin.order,
      detectTolerance: state.pin.detectTolerance,
      detectMinArea: state.pin.detectMinArea,
    },
    atlas: {
      layoutMode: state.atlas.layoutMode,
    },
  };
}

type ToolField = 'wandTolerance' | 'brushRadius' | 'brushSoftness';
type HistoryBackground = Omit<Settings['background'], ToolField> & { regions: KeyedRegion[] };
type HistoryWorkflow = Omit<Settings['workflow'], 'step'>;
export type History = Omit<Settings, 'background' | 'workflow'> & {
  background: HistoryBackground;
  workflow: HistoryWorkflow;
};

export function pickHistory(state: RootState): History {
  const s = pickSettings(state);
  const { wandTolerance: _w, brushRadius: _r, brushSoftness: _sf, ...rest } = s.background;
  const { step: _step, ...workflowRest } = s.workflow;
  return {
    ...s,
    background: { ...rest, regions: state.background.regions },
    workflow: workflowRest,
  };
}

function rectEqual(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

function numbersEqual(a: number[] | null, b: number[] | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function regionsEqual(a: KeyedRegion[], b: KeyedRegion[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ra = a[i];
    const rb = b[i];
    if (!ra || !rb) return false;
    if (ra.id !== rb.id || ra.tolerance !== rb.tolerance) return false;
    // Mask is replaced (not mutated in place) on every region edit, so reference
    // identity is enough — covers shift-erase, redraw, and any subtractStroke output.
    if (ra.mask !== rb.mask || ra.pixelCount !== rb.pixelCount) return false;
  }
  return true;
}

function bgColorsEqual(a: BgColor[], b: BgColor[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const aC = a[i];
    const bC = b[i];
    if (!aC || !bC) return false;
    if (aC.rgb[0] !== bC.rgb[0] || aC.rgb[1] !== bC.rgb[1] || aC.rgb[2] !== bC.rgb[2]) return false;
    if (aC.weight !== bC.weight) return false;
  }
  return true;
}

function coreEqual(a: Settings | History, b: Settings | History): boolean {
  return (
    a.background.enabled === b.background.enabled &&
    a.background.tolerance === b.background.tolerance &&
    a.background.hardEdge === b.background.hardEdge &&
    a.background.erode === b.background.erode &&
    a.background.feather === b.background.feather &&
    a.background.despillStrength === b.background.despillStrength &&
    a.background.despillRadius === b.background.despillRadius &&
    a.background.floodFill === b.background.floodFill &&
    a.background.maxEnclosedIsland === b.background.maxEnclosedIsland &&
    bgColorsEqual(a.background.bgColors, b.background.bgColors) &&
    rectEqual(a.grid.cropRect, b.grid.cropRect) &&
    numbersEqual(a.grid.rowDividers, b.grid.rowDividers) &&
    numbersEqual(a.grid.colDividers, b.grid.colDividers) &&
    a.grid.individualRows === b.grid.individualRows &&
    a.grid.individualCols === b.grid.individualCols &&
    a.editor.showRegions === b.editor.showRegions &&
    a.editor.snapToGuidelines === b.editor.snapToGuidelines &&
    numbersEqual(a.editor.guidelines.vertical, b.editor.guidelines.vertical) &&
    numbersEqual(a.editor.guidelines.horizontal, b.editor.guidelines.horizontal) &&
    rectEqual(a.watermark.region, b.watermark.region) &&
    a.watermark.enabled === b.watermark.enabled &&
    a.watermark.useOpencv === b.watermark.useOpencv &&
    clipsEqual(a.clips.list, b.clips.list) &&
    a.clips.activeId === b.clips.activeId &&
    a.frames.pivot.x === b.frames.pivot.x &&
    a.frames.pivot.y === b.frames.pivot.y &&
    a.animation.onionStack === b.animation.onionStack &&
    a.source.hash === b.source.hash &&
    a.workflow.committedKey === b.workflow.committedKey &&
    a.pin.framingMode === b.pin.framingMode &&
    a.pin.overrides === b.pin.overrides &&
    a.pin.order === b.pin.order &&
    a.pin.detectTolerance === b.pin.detectTolerance &&
    a.pin.detectMinArea === b.pin.detectMinArea &&
    layoutModeEqual(a.atlas.layoutMode, b.atlas.layoutMode)
  );
}

function layoutModeEqual(a: LayoutMode, b: LayoutMode): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'grid' && b.kind === 'grid') {
    return a.rows === b.rows && a.cols === b.cols && a.fill === b.fill;
  }
  if (a.kind === 'free' && b.kind === 'free') {
    // positions object is replaced (not mutated) on every setFreePosition call, so
    // reference identity is sufficient — mirrors how pin.overrides is handled.
    return a.positions === b.positions;
  }
  return true; // sequence
}

export function settingsEqual(a: Settings, b: Settings): boolean {
  return (
    coreEqual(a, b) &&
    a.workflow.step === b.workflow.step &&
    a.background.wandTolerance === b.background.wandTolerance &&
    a.background.brushRadius === b.background.brushRadius &&
    a.background.brushSoftness === b.background.brushSoftness
  );
}

export function historyEqual(a: History, b: History): boolean {
  return coreEqual(a, b) && regionsEqual(a.background.regions, b.background.regions);
}
